import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, ChevronLeft, ChevronRight, Info, Megaphone, Wrench, WifiOff, X } from "lucide-react";
import api from "@/lib/api";

export const NOTICE_STYLES = {
  outage: { bg: "#E63946", fg: "#fff", Icon: WifiOff, label: "Outage (red)" },
  delay: { bg: "#FFC107", fg: "#1a1a1a", Icon: AlertTriangle, label: "Delay (amber)" },
  maintenance: { bg: "#0047AB", fg: "#fff", Icon: Wrench, label: "Maintenance (blue)" },
  promo: { bg: "#00875A", fg: "#fff", Icon: Megaphone, label: "Promotion (green)" },
  info: { bg: "#0A0A0A", fg: "#fff", Icon: Info, label: "Info (dark)" },
};
const SLIDE_MS = 6000;

const SlideLink = ({ slide, fg }) => {
  if (!slide.link) return null;
  const cls = "inline-flex items-center gap-1 font-semibold underline underline-offset-2 whitespace-nowrap hover:opacity-80";
  const label = <>{slide.link_label || "Find out more"}<ArrowRight className="w-3.5 h-3.5" /></>;
  return slide.link.startsWith("/")
    ? <Link to={slide.link} className={cls} style={{ color: fg }} data-testid="site-notice-link">{label}</Link>
    : <a href={slide.link} target="_blank" rel="noreferrer" className={cls} style={{ color: fg }} data-testid="site-notice-link">{label}</a>;
};

export const SiteNoticeBanner = () => {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dismissed, setDismissed] = useState(() => new Set());

  useEffect(() => {
    const load = () => api.get("/public/site-notice").then((r) => setData(r.data || {})).catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, []);

  const slides = useMemo(() => {
    const out = [];
    if (data?.id) out.push({ id: data.id, message: data.message, style: data.severity, status: data.status, incident: true });
    (data?.announcements || []).forEach((a) => out.push({ id: a.id, message: a.message, style: a.style, link: a.link, link_label: a.link_label }));
    return out.filter((s) => !dismissed.has(s.id) && !sessionStorage.getItem(`notice_${s.id}`));
  }, [data, dismissed]);

  useEffect(() => {
    if (slides.length < 2 || paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), SLIDE_MS);
    return () => clearInterval(t);
  }, [slides.length, paused]);

  useEffect(() => { if (idx >= slides.length) setIdx(0); }, [slides.length, idx]);

  if (!slides.length) return null;
  const cur = slides[Math.min(idx, slides.length - 1)];
  const s = NOTICE_STYLES[cur.style] || NOTICE_STYLES.info;
  const { Icon } = s;
  const many = slides.length > 1;
  const go = (d) => setIdx((i) => (i + d + slides.length) % slides.length);
  const dismiss = () => { sessionStorage.setItem(`notice_${cur.id}`, "1"); setDismissed((prev) => new Set([...prev, cur.id])); };

  return (
    <div className="relative overflow-hidden" style={{ background: s.bg, color: s.fg, transition: "background-color .5s ease" }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      data-testid="site-notice-banner" data-slide-count={slides.length} data-slide-style={cur.style}>
      <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm px-4 py-2.5">
        {many && (
          <button onClick={() => go(-1)} className="shrink-0 opacity-70 hover:opacity-100 p-0.5" aria-label="Previous notice" data-testid="site-notice-prev"><ChevronLeft className="w-4 h-4" /></button>
        )}
        <div key={cur.id} className="flex-1 flex items-center gap-3 min-w-0 notice-slide" data-testid="site-notice-slide">
          <Icon className="w-4 h-4 shrink-0" />
          <span className="flex-1 min-w-0" data-testid="site-notice-message">{cur.message}</span>
          <SlideLink slide={cur} fg={s.fg} />
          {cur.incident && cur.status && cur.status !== "investigating" && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 shrink-0" style={{ background: "rgba(0,0,0,0.2)" }}>{cur.status}</span>
          )}
        </div>
        {many && (
          <>
            <button onClick={() => go(1)} className="shrink-0 opacity-70 hover:opacity-100 p-0.5" aria-label="Next notice" data-testid="site-notice-next"><ChevronRight className="w-4 h-4" /></button>
            <div className="hidden sm:flex items-center gap-1 shrink-0 ml-1" data-testid="site-notice-dots">
              {slides.map((sl, i) => (
                <button key={sl.id} onClick={() => setIdx(i)} aria-label={`Notice ${i + 1}`} className="w-1.5 h-1.5 rounded-full transition-opacity"
                  style={{ background: s.fg, opacity: i === idx ? 1 : 0.35 }} data-testid={`site-notice-dot-${i}`} />
              ))}
            </div>
          </>
        )}
        <button onClick={dismiss} className="shrink-0 opacity-70 hover:opacity-100 ml-1" aria-label="Dismiss notice" data-testid="site-notice-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
      {many && !paused && (
        <div key={`${cur.id}-${idx}`} className="absolute bottom-0 left-0 h-[2px] notice-progress" style={{ background: s.fg, opacity: 0.55, animationDuration: `${SLIDE_MS}ms` }} />
      )}
    </div>
  );
};
